import clsx from 'clsx';
import dayjs from 'dayjs';
import _ from 'lodash';
import { getCellColor } from '../../../helpers/heat-map-helper';
import type { FixMeLater } from '../../../types/fix-me-later-type';
import { TableWidget } from '../../table-widget';

type Props = {
  table: string;
  roomData: RoomData;
  scrollDates: Function;
  loading: boolean;
};

export type RoomType = {
  room_category: string;
  room_code: string;
  rt_details: string;
  rt_sold: number;
  rt_remaining: number;
  rt_room_allocation: number;
  rt_flex_rate: number;
  rt_status: string;
  occupancy: any;
  stay_date: string;
  rt_flex_rate_raw?: number; // allow raw flex rate for tooltip
};

export type RoomColumn = {
  room_types: RoomType[];
  stay_date: string;
};

type RoomListHeader = {
  header: string;
  order: number;
  tooltip: string;
};

export type RoomData = {
  allRoomData: RoomColumn[];
  roomList: RoomListHeader[];
};

function RoomsTable({ table, roomData, scrollDates, loading }: Props) {
  const tableKey = getTableKey();
  const tableName = getTableName();

  function getTableKey() {
    switch (table) {
      case 'sold':
        return 'rt_sold';
      case 'remaining':
        return 'rt_remaining';
      case 'occupancy':
        return 'occupancy';
      case 'allocation':
        return 'rt_room_allocation';
      case 'flex':
        return 'rt_flex_rate';
      default:
        return 'rt_sold';
    }
  }

  function getTableName() {
    switch (table) {
      case 'sold':
        return 'Sold';
      case 'remaining':
        return 'Remaining';
      case 'occupancy':
        return 'Occupancy %';
      case 'allocation':
        return 'Allocation';
      case 'flex':
        return 'Flex Rate';
      default:
        return '';
    }
  }

  function cellColor(cellData: RoomType) {
    const { rt_sold, rt_remaining, rt_room_allocation, rt_status, rt_flex_rate } = cellData;

    switch (table) {
      case 'sold':
        return getCellColor(rt_sold, rt_status);
      case 'remaining':
        return getCellColor(rt_remaining, rt_status);
      case 'allocation':
        getMinMax();
        return getCellColor(rt_room_allocation, rt_status);
      case 'occupancy':
        return getCellColor(cellData.occupancy, rt_status);
      case 'flex':
        return getCellColor(rt_flex_rate, rt_status);
      default:
        return 'bg-white';
    }
  }

  function getMinMax() {
    const minArray: Array<number> = [];
    const maxArray: Array<number> = [];

    const getDateMin = (roomTypes: RoomType[]) => {
      const dateMin = _.minBy(roomTypes, (room: RoomType) => room[tableKey]);

      if (dateMin) {
        minArray.push(dateMin[tableKey]);
      }
    };

    const getDateMax = (roomTypes: RoomType[]) => {
      const dateMax = _.maxBy(roomTypes, (room: RoomType) => room[tableKey]);

      if (dateMax) {
        maxArray.push(dateMax[tableKey]);
      }
    };

    if (allRoomData) {
      allRoomData.forEach((date: RoomColumn) => {
        getDateMin(date.room_types);
        getDateMax(date.room_types);
      });
    }

    const minValue: number = _.min(minArray) || 0;
    const maxValue: number = _.max(maxArray) || 0;

    return [minValue, maxValue];
  }

  const allRoomData = _.cloneDeep(roomData)?.allRoomData.sort((a: RoomColumn, b: RoomColumn) => {
    return dayjs(b.stay_date).isBefore(dayjs(a.stay_date)) ? 1 : -1;
  });

  const roomList = roomData?.roomList;
  const roomRows = [];
  let roomTooltips;

  // Format Table Data (aligned to all dates)
  if (allRoomData && roomList) {
    // Build the master date array (ensure uniqueness)
    const allDates = _.uniq(allRoomData.map((col: RoomColumn) => col.stay_date));
    allDates.sort((a, b) => (dayjs(a).isAfter(dayjs(b)) ? 1 : -1));
    // Map: room_code -> { [stay_date]: RoomType }
    const roomTypeDateMap = new Map();
    roomList.forEach((room: RoomListHeader) => {
      roomTypeDateMap.set(room.header, {});
    });

    // Populate date-mapped data for each room type
    allRoomData.forEach((date: RoomColumn) => {
      date.room_types.forEach((type: RoomType) => {
        const dateMap = roomTypeDateMap.get(type.room_code);
        if (dateMap) {
          let value = type;
          if (tableKey === 'occupancy') {
            const roomTotal = type.rt_room_allocation;
            const sold = type.rt_sold;
            const occPercent = roomTotal === 0 ? 0 : Math.round((sold / roomTotal) * 100);
            value = { ...type, occupancy: occPercent };
          } else if (tableKey === 'rt_flex_rate') {
            value = {
              ...type,
              rt_flex_rate: Math.round(type.rt_flex_rate),
              rt_flex_rate_raw: type.rt_flex_rate,
            };
          }
          dateMap[date.stay_date] = value;
        }
      });
    });

    // For each room type, build a row aligned to all dates
    for (const room of roomList) {
      const dateMap = roomTypeDateMap.get(room.header);
      // Row: [room_code, ...cells for each date]
      const row = [room.header];
      for (const date of allDates) {
        row.push(dateMap[date] || null);
      }
      roomRows.push(row);
    }
  }

  if (roomList) {
    const roomTooltipMap = new Map();

    roomList.forEach((room: FixMeLater) => {
      roomTooltipMap.set(room.header, room.tooltip);
    });

    roomTooltips = roomTooltipMap;
  }

  return (
    <div className={`flex flex-col w-full overflow-auto ${clsx(table === 'flex' ? null : 'mb-5')}`}>
      <TableWidget
        tableName={tableName}
        cellColor={cellColor}
        data={allRoomData}
        roomTooltips={roomTooltips}
        rowData={roomRows}
        scrollDates={scrollDates}
        tableKey={tableKey}
        loading={loading}
        key={tableName}
        page='Room Type'
      />
    </div>
  );
}

export default RoomsTable;
